import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ContentCard from "../components/ContentCard";
import DetailsHeader from "../components/DetailsHeader";
import { fetchCompaniesAll } from "../utils/api";
import { apiRequest } from "../utils/apiRequest";
import { showToast } from "../utils/toastOptions";
import { usePermissionTemplate } from "../utils/usePermissionTemplate";
import { useUserSession } from "../utils/useUserSession";

export default function Settings() {
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUserSession();
  const { template: permissionTemplate } = usePermissionTemplate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [companyMap, setCompanyMap] = useState({});

  const adminCompanies = useMemo(() => {
    if (!permissionTemplate) return [];
    return Object.entries(permissionTemplate)
      .filter(([, val]) => val?.user_access_levels?.admin === 4)
      .map(([key, val]) => {
        const cid = Number.parseInt(key, 10) || key;
        const nameFromMap = companyMap[cid]?.name;
        const nameFromTpl = val?.company_name || val?.name;
        return {
          id: cid,
          name: nameFromMap || nameFromTpl || `Company ${key}`,
        };
      });
  }, [permissionTemplate, companyMap]);

  useEffect(() => {
    const load = async () => {
      try {
        const [prefsJson, companies] = await Promise.all([
          apiRequest(`${process.env.REACT_APP_API_BASE_URL}/notification-preferences/`, {
            method: "GET",
          }),
          fetchCompaniesAll(true),
        ]);

        if (user) {
          setName(user?.name || "");
          setEmail(user?.email_address || "");
        }

        setPrefs(prefsJson?.preferences || {});

        if (Array.isArray(companies)) {
          const cmap = {};
          companies.forEach((c) => {
            if (c?.id) cmap[c.id] = { id: c.id, name: c.name || "" };
          });
          setCompanyMap(cmap);
        }
      } catch (e) {
        showToast("error", e.message || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (userLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleTogglePref = async (companyId) => {
    const current = prefs[String(companyId)];
    const next = current === undefined ? false : !current;
    setPrefs((p) => ({ ...p, [companyId]: next }));
    setSavingPrefs(true);
    try {
      await apiRequest(`${process.env.REACT_APP_API_BASE_URL}/notification-preferences/`, {
        method: "POST",
        body: {
          company_id: companyId,
          enabled: next,
        },
      });
    } catch (e) {
      showToast("error", e.message || "Failed to update preference");
      // revert on error
      setPrefs((p) => ({ ...p, [companyId]: current }));
    } finally {
      setSavingPrefs(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <main className="page dash-wrapper">
      <Box sx={{ py: 3, px: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
        <DetailsHeader title="Settings" onBack={() => navigate(-1)} showRefresh={false} />

        <ContentCard sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Profile
          </Typography>
          <Stack spacing={2} maxWidth={420}>
            <TextField
              label="Name"
              size="small"
              value={name}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Email"
              size="small"
              value={email}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </Stack>
        </ContentCard>

        <ContentCard sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Notifications
          </Typography>
          {adminCompanies.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No admin companies found.
            </Typography>
          ) : (
            <Stack spacing={1} divider={<Divider flexItem />}>
              {adminCompanies.map((c) => {
                const enabled = prefs[String(c.id)];
                const isOn = enabled === undefined ? true : !!enabled;
                return (
                  <FormControlLabel
                    key={c.id}
                    control={
                      <Switch
                        checked={isOn}
                        onChange={() => handleTogglePref(c.id)}
                        disabled={savingPrefs}
                      />
                    }
                    label={`${c.name} (ID: ${c.id})`}
                  />
                );
              })}
            </Stack>
          )}
        </ContentCard>
      </Box>
    </main>
  );
}
